import React, {createRef, Component} from "react"
import {connect} from "react-redux"
import {set, get, isNull} from "lodash"

import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {H1, Body} from "@irati/ui/src/Typography"
import Success from "@irati/ui/src/Success"
import {IconButton} from "@irati/ui/src/Button"
import selfSignUpService from "@irati-web/platform/src/services/selfSignUp"
import Loading from "@irati/ui/src/Loading"
import Spacing from "@irati/ui/src/Spacing"
import mfaService from "@irati/auth/services/mfa"
import {Tenant} from "@irati/types"
import {ReduxState} from "@irati/store/types"

import SuccessWithBackLink from "../../../../components/SuccessWithBackLink"
import SelfSignUpPopUp from "../../../../components/Settings/CompanySignUpSettings/SelfSignUp/SelfSignUpPopup"
import DoNotAllow from "../../../../components/Settings/CompanySignUpSettings/SelfSignUp/DoNotAllow"
import EmailDomain from "../../../../components/Settings/CompanySignUpSettings/SelfSignUp/EmailDomain"
import SecretCode from "../../../../components/Settings/CompanySignUpSettings/SelfSignUp/SecretCode"
import SignUpLink from "../../../../components/Settings/CompanySignUpSettings/SelfSignUp/SignUpLink"
import {CODE, EMAIL, NONE, WELCOME_MESSAGE} from "../../../../constants/selfSignUp"

interface Props extends InjectIntl {
  userTenant: Tenant,
  onHide: () => void,
  goBack: () => void,
  showBackIcon?: boolean,
  backLink?: () => void
}

interface State {
  selfSignUp: {tenant: string, welcomeMessage: string, method?: string},
  show: string,
  savedMessage: string | null,
  load: Promise<void> | null,
  submit: Promise<void> | null,
  changed: boolean,
  saved?: boolean
}

class Index extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      selfSignUp: {
        tenant: props.userTenant.uuid,
        welcomeMessage: props.formatMessage({id: WELCOME_MESSAGE})
      },
      show: NONE,
      savedMessage: null,
      load: null,
      submit: null,
      changed: false
    }
  }
  static defaultProps = {
    showBackIcon: true
  }

  selfSignUpPopup = createRef<SelfSignUpPopUp>()

  componentDidMount = () => {
    const load = selfSignUpService
      .getSelfSignupByTenant(this.props.userTenant)
      .then((data) => {
        this.setState({
          selfSignUp: data,
          show: data.method
        })
      })
      .catch(() => {
        /* no-op, fail silently */
      })
    this.setState({load})
  }

  get = () => {
    selfSignUpService.getSelfSignupByTenant(this.props.userTenant).then((data) => {
      this.setState({
        selfSignUp: data
      })
      this.selfSignUpPopup.current.popup.close()
    })
  }

  handleChange = (key: string, value: string) => {
    const {selfSignUp} = this.state
    set(selfSignUp, key, value)
    this.setState({selfSignUp, changed: true})
  }

  handleSelect = (show: string) => {
    if (show !== this.state.show) {
      if (this.state.changed) {
        this.selfSignUpPopup.current.open(show)
      } else {
        this.setState({show})
      }
    }
  }

  handleSubmit = (save: boolean = true) => {
    const {formatMessage} = this.props
    const selfSignUp = this.state.selfSignUp

    selfSignUp.welcomeMessage = isNull(selfSignUp.welcomeMessage) ? formatMessage({id: WELCOME_MESSAGE}) : selfSignUp.welcomeMessage

    const submit = mfaService.hasFlag().then(() => {
      return selfSignUpService
        .createOrUpdateSelfSignUp(selfSignUp, get(this.state.selfSignUp, "uuid"))
        .then((data) => {
          this.setState({selfSignUp: data})
          if (save) {
            this.save()
          } else {
            this.setState({
              changed: false
            })
          }
        })
        .catch((err: PBError) => {
          this.setState({
            show: this.state.selfSignUp.method
          })
          throw err
        })
    })
    this.setState({submit})
  }

  handlePopUp = (show: string, shouldSave: boolean) => {
    if (shouldSave) {
      this.handleSubmit(false)
    } else {
      this.get()
    }
    this.setState({show, changed: false})
  }

  save = () => {
    const {onHide, showBackIcon} = this.props
    this.setState({saved: true})
    setTimeout(() => {
      if (showBackIcon) {
        onHide()
      }
      this.setState({saved: false})
    }, 3000)
  }

  render() {
    const {saved, submit, show, load, selfSignUp} = this.state
    const {formatMessage, showBackIcon, backLink} = this.props

    if (saved) {
      return (
        <>
          {showBackIcon ? (
            <Success message={formatMessage({id: "admin.containers.selfSignUp.success.message"})} />
          ) : (
            <SuccessWithBackLink message={formatMessage({id: "admin.containers.selfSignUp.success.message"})} backLink={backLink} />
          )}
        </>
      )
    }
    return (
      <>
        <Loading load={load}>
          {showBackIcon && <IconButton onClick={this.props.goBack} icon="backArrow" />}
          <>
            <H1 marginBottom={4}>
              <FormattedMessage id="admin.containers.selfSignUp.headingTwo.text" />
            </H1>
            {!selfSignUp || selfSignUp.method === NONE ? (
              <Body>
                <FormattedMessage id="admin.containers.selfSignUp.headingFour.text" />
              </Body>
            ) : (
              <SignUpLink />
            )}
            <Spacing marginTop={6}>
              <DoNotAllow
                show={!show || show === NONE}
                submit={submit}
                onSelect={this.handleSelect}
                selfSignUp={selfSignUp}
                onChange={this.handleChange}
                onSubmit={this.handleSubmit} />
              <EmailDomain
                show={show === EMAIL}
                submit={submit}
                onSelect={this.handleSelect}
                selfSignUp={selfSignUp}
                onChange={this.handleChange}
                onSubmit={this.handleSubmit} />
              <SecretCode
                show={show === CODE}
                submit={submit}
                onSelect={this.handleSelect}
                selfSignUp={this.state.selfSignUp}
                onChange={this.handleChange}
                onSubmit={this.handleSubmit} />
            </Spacing>
          </>
        </Loading>
        <SelfSignUpPopUp ref={this.selfSignUpPopup} onChange={this.handlePopUp} />
      </>
    )
  }
}

const mapStateToProps = ({auth}: ReduxState) => {
  return {
    userTenant: auth.userTenant
  }
}

export default injectIntl(connect(mapStateToProps)(Index))
